import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../common/layout/Layout';
import './PaymentSuccessPage.css';

interface PaymentStatus {
  status: 'success' | 'failed' | 'processing';
  orderId: string;
  amount: string;
  currency: string;
  packageName?: string;
  message: string;
}

const PaymentSuccessPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Check if user is authenticated, if not - try to restore session first
        if (!authLoading && !isAuthenticated) {
          console.log('User not authenticated after payment redirect, checking for session recovery...');
          
          // First try to restore from sessionStorage (stored before payment)
          const prePaymentToken = sessionStorage.getItem('pre_payment_token');
          const prePaymentUser = sessionStorage.getItem('pre_payment_user');
          
          if (prePaymentToken && prePaymentUser) {
            console.log('Found pre-payment session, restoring...');
            // Restore to localStorage
            localStorage.setItem('auth_token', prePaymentToken);
            localStorage.setItem('auth_user', prePaymentUser);
            // Clear sessionStorage
            sessionStorage.removeItem('pre_payment_token');
            sessionStorage.removeItem('pre_payment_user');
            // Wait a bit for auth to process
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Check if there's a token in URL (fallback method)
            const urlToken = new URLSearchParams(window.location.search).get('token');
            if (urlToken) {
              console.log('Found token in URL, restoring session...');
              localStorage.setItem('auth_token', urlToken);
              // Clean URL
              const newUrl = window.location.pathname + window.location.search.replace(/[?&]token=[^&]*/, '');
              window.history.replaceState({}, document.title, newUrl);
              // Wait a bit for auth to process
              await new Promise(resolve => setTimeout(resolve, 1000));
            } else {
              console.log('No session recovery possible, user will need to login manually');
            }
          }
        }
        
        // Get order ID from URL parameters or sessionStorage
        const urlParams = new URLSearchParams(window.location.search);
        let orderId = urlParams.get('order_id');
        
        // If no order_id in URL, try to get from sessionStorage
        if (!orderId) {
          orderId = sessionStorage.getItem('pending_order_id');
          if (orderId) {
            sessionStorage.removeItem('pending_order_id');
            // Update URL with order_id for better UX
            const newUrl = `${window.location.pathname}?order_id=${orderId}`;
            window.history.replaceState({}, '', newUrl);
          }
        }
        
        if (!orderId) {
          // If no order_id, show generic success message for direct access
          setPaymentStatus({
            status: 'success',
            orderId: 'unknown',
            amount: '0',
            currency: 'UAH',
            message: t('paymentSuccessMessage', 'payment')
          });
          setLoading(false);
          return;
        }

        // Check payment status from public API (no authentication required)
        const response = await fetch(`/api/v1/payment/status/public/${orderId}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentStatus({
            status: data.status === 'completed' ? 'success' : 
                   data.status === 'failed' ? 'failed' : 'processing',
            orderId: data.order_id,
            amount: data.amount || '0',
            currency: data.currency || 'UAH',
            message: data.status_details?.client_message || t('paymentProcessing')
          });
        } else {
          setError('Failed to check payment status');
        }
      } catch (err) {
        setError('Error checking payment status');
        console.error('Payment status check error:', err);
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [t]);

  const getStatusIcon = () => {
    if (loading) return <CircularProgress size={60} />;
    
    switch (paymentStatus?.status) {
      case 'success':
        return <CheckCircleIcon className="success-icon" />;
      case 'failed':
        return <ErrorIcon className="error-icon" />;
      default:
        return <CreditCardIcon className="processing-icon" />;
    }
  };

  const getStatusTitle = () => {
    if (loading) return t('checkingPayment', 'payment');
    
    switch (paymentStatus?.status) {
      case 'success':
        return t('paymentSuccessful', 'payment');
      case 'failed':
        return t('paymentFailed', 'payment');
      default:
        return t('paymentProcessing', 'payment');
    }
  };

  const getStatusMessage = () => {
    if (loading) return t('pleaseWait', 'payment');
    
    switch (paymentStatus?.status) {
      case 'success':
        return t('paymentSuccessMessage', 'payment');
      case 'failed':
        return t('paymentFailedMessage', 'payment');
      default:
        return t('paymentProcessingMessage', 'payment');
    }
  };

  const handleGoHome = () => {
    navigate('/client/subscription?tab=subscriptions');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  if (error) {
    return (
      <Layout>
        <Container maxWidth="sm">
          <Box className="payment-success-container">
            <Card className="payment-card">
              <CardContent className="payment-card-content">
                <ErrorIcon className="error-icon" />
                <Typography variant="h4" className="payment-title">
                  {t('error', 'payment')}
                </Typography>
                <Typography variant="body1" className="payment-message">
                  {error}
                </Typography>
                <Box className="payment-actions">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGoHome}
                    startIcon={<HomeIcon />}
                  >
                    {t('goToDashboard', 'payment')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="sm">
        <Box className="payment-success-container">
          <Card className="payment-card">
            <CardContent className="payment-card-content">
              <Box className="payment-icon">
                {getStatusIcon()}
              </Box>
              
              <Typography variant="h4" className="payment-title">
                {getStatusTitle()}
              </Typography>
              
              <Typography variant="body1" className="payment-message">
                {getStatusMessage()}
              </Typography>

              {paymentStatus && (
                <Box className="payment-details">
                  {paymentStatus.orderId !== 'unknown' && (
                    <Chip 
                      label={`Order ID: ${paymentStatus.orderId}`}
                      variant="outlined"
                      size="small"
                      className="order-chip"
                    />
                  )}
                  {paymentStatus.amount && paymentStatus.amount !== '0' && (
                    <Typography variant="body2" className="payment-amount">
                      Amount: {paymentStatus.amount} {paymentStatus.currency}
                    </Typography>
                  )}
                </Box>
              )}

              <Box className="payment-actions">
                {paymentStatus?.status === 'processing' && (
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    className="refresh-button"
                  >
                    {t('refresh', 'payment')}
                  </Button>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGoHome}
                  startIcon={<HomeIcon />}
                  className="home-button"
                >
                  {t('goToDashboard', 'payment')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Layout>
  );
};

export default PaymentSuccessPage;
